import { useMutation, useQueryClient } from "@tanstack/react-query";

import { useDomainServices } from "@/providers/DomainServices";
import { modelProfilesQueryKey } from "@/shared/domain/agentThread/hooks/useModelProfiles";
import { RobotoConflictException } from "@/shared/http";

import type {
  OrgModelListResponse,
  UpdateOrgModelListRequest,
} from "../OrgLlmProviderRecord";

import { orgQueryKeys } from "./orgQueryKeys";

interface UpdateOrgLlmProviderVariables {
  orgId: string;
  request: UpdateOrgModelListRequest;
}

/**
 * Replace an org's chat model list. On success the stored list (with the
 * org's connections) replaces the cached one, and the org's chat model
 * profiles and the admin model-list searches are refetched so pickers and
 * tables show the new models. When the list was saved elsewhere since it
 * was loaded (a 409), the stored list is refetched.
 */
export function useUpdateOrgLlmProvider() {
  const { orgService } = useDomainServices();
  const queryClient = useQueryClient();

  return useMutation<
    OrgModelListResponse,
    Error,
    UpdateOrgLlmProviderVariables
  >({
    mutationFn: ({ orgId, request }) =>
      orgService.updateOrgLlmProvider(orgId, request),
    onSuccess: (response, { orgId }) => {
      queryClient.setQueryData(orgQueryKeys.llmProvider(orgId), response);
      void queryClient.invalidateQueries({
        queryKey: modelProfilesQueryKey(orgId),
      });
      void queryClient.invalidateQueries({
        queryKey: orgQueryKeys.modelConfigSearches(),
      });
    },
    onError: (error, { orgId }) => {
      if (error instanceof RobotoConflictException) {
        void queryClient.invalidateQueries({
          queryKey: orgQueryKeys.llmProvider(orgId),
        });
      }
    },
  });
}
